/**
 * What the Insights grid shows right now (CT-5716). One value, not four
 * booleans whose interaction has to be re-derived at every reading — the same
 * shape as `traitsListState` (CT-5715).
 *
 * `noPattern`, `noResults` and `empty` are all "no cards" and are deliberately
 * different: with no pattern yet the emerging insights ARE the content (under a
 * notice); telling someone to clear filters they never set is noise.
 */
export const INSIGHTS_LIST_STATE = Object.freeze({
  LOADING: 'loading',
  ACCESS_DENIED: 'accessDenied',
  ERROR: 'error',
  /** Nothing passed the threshold yet; the server says how many interactions there are. */
  NO_PATTERN: 'noPattern',
  /** There are insights, but none the chip, filters or search let through. */
  NO_RESULTS: 'noResults',
  /** Nothing at all, and nothing narrowed. */
  EMPTY: 'empty',
  /** Cards, the "Show emerging" toggle, or both. */
  ROWS: 'rows',
});

/**
 * The order matters and is the product rule:
 *
 * A failure outranks emptiness — an empty grid drawn over a failed request
 * claims the person has no insights when nobody asked successfully. But a
 * failure does not take away what is already on screen: as in the prototype,
 * the last cards stay under the "Couldn't load insights · Retry" row (the page
 * draws that row whenever `error` is set). A card above
 * the threshold is content whatever else is true. "No pattern yet" needs the
 * interaction count from the server and only applies when the user narrowed
 * nothing away. After that, emerging insights alone still make a grid (behind
 * their toggle).
 *
 * @param {object} args
 * @param {boolean} args.loading
 * @param {boolean} args.accessDenied
 * @param {string|null} args.error
 * @param {number} args.itemCount — cards above the threshold.
 * @param {number} args.emergingCount
 * @param {number|null} args.interactionsSoFar
 * @param {boolean} args.narrowed — a chip, a filter or a search is on.
 * @returns {string} one of `INSIGHTS_LIST_STATE`.
 */
export const insightsListState = ({
  loading,
  accessDenied,
  error,
  itemCount,
  emergingCount,
  interactionsSoFar,
  narrowed,
}) => {
  if (loading) return INSIGHTS_LIST_STATE.LOADING;
  if (accessDenied) return INSIGHTS_LIST_STATE.ACCESS_DENIED;
  if (error && itemCount === 0 && emergingCount === 0) return INSIGHTS_LIST_STATE.ERROR;

  if (itemCount > 0) return INSIGHTS_LIST_STATE.ROWS;
  if (!narrowed && interactionsSoFar != null) return INSIGHTS_LIST_STATE.NO_PATTERN;
  if (emergingCount > 0) return INSIGHTS_LIST_STATE.ROWS;
  if (narrowed) return INSIGHTS_LIST_STATE.NO_RESULTS;
  return INSIGHTS_LIST_STATE.EMPTY;
};

/** The chips are hidden only while no pattern holds: there is nothing to narrow yet. */
export const showsInsightQuickFilters = (state) => state !== INSIGHTS_LIST_STATE.NO_PATTERN;

/** The request has come back, one way or another. */
export const isInsightsListSettled = (state) =>
  state !== INSIGHTS_LIST_STATE.LOADING &&
  state !== INSIGHTS_LIST_STATE.ERROR &&
  state !== INSIGHTS_LIST_STATE.ACCESS_DENIED;
